import { useState, useEffect, useCallback } from "react";
import useProviderLinks from "../../components/provider/useProviderLinks";
import ProviderOrganizationsCell from "../../components/provider/ProviderOrganizationsCell";
import {
  Container,
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { apiService } from "../../services/api";

export default function ManagePractices() {
  const [practices, setPractices] = useState([]);
  const [providers, setProviders] = useState([]);
  const { linkFor, reload: reloadProviderLinks } = useProviderLinks();
  const [pendingPatientRequests, setPendingPatientRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [newPractice, setNewPractice] = useState({ name: "", addressLine1: "", city: "", state: "", zip: "", phone: "" });
  const [creatingPractice, setCreatingPractice] = useState(false);
  const [rowActionId, setRowActionId] = useState(null); // tracks which row's action is in-flight, for per-row spinners
  const [message, setMessage] = useState(null); // { text, severity }

  const loadAll = useCallback(async () => {
    try {
      const [practicesData, providersData, patientRequestsData] = await Promise.all([
        apiService.getAllPractices(),
        apiService.getAllProviderAccounts(),
        apiService.getPendingPatientPracticeRequests(),
      ]);
      setPractices(practicesData || []);
      setProviders(providersData || []);
      setPendingPatientRequests(patientRequestsData || []);
    } catch (err) {
      console.error("Failed to load practice/provider data:", err);
      setError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const showMessage = (text, severity = "success") => {
    setMessage({ text, severity });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleCreatePractice = async () => {
    if (!newPractice.name.trim()) return;
    setCreatingPractice(true);
    try {
      await apiService.createPractice(newPractice);
      setNewPractice({ name: "", addressLine1: "", city: "", state: "", zip: "", phone: "" });
      showMessage("Practice created.");
      await loadAll();
    } catch (err) {
      showMessage(err.response?.data?.error || "Failed to create practice.", "error");
    } finally {
      setCreatingPractice(false);
    }
  };

  const handleDeletePractice = async (organizationId, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setRowActionId(organizationId);
    try {
      await apiService.deletePractice(organizationId);
      showMessage(`"${name}" deleted.`);
      await loadAll();
    } catch (err) {
      // The backend refuses deletion with a specific reason (providers or
      // patients still assigned) — surface that exact message rather than
      // a generic failure, since it tells the admin what to fix first.
      showMessage(err.response?.data?.error || "Failed to delete practice.", "error");
    } finally {
      setRowActionId(null);
    }
  };

  const handleToggleBlock = async (provider) => {
    setRowActionId(provider.id);
    try {
      if (provider.blocked) {
        await apiService.unblockProvider(provider.id);
        showMessage(`${provider.name} unblocked.`);
      } else {
        if (!window.confirm(`Block ${provider.name}? They will be reassigned to Default Practice and lose all data access until unblocked.`)) {
          setRowActionId(null);
          return;
        }
        await apiService.blockProvider(provider.id);
        showMessage(`${provider.name} blocked.`);
      }
      await loadAll();
    } catch (err) {
      showMessage("Failed to update provider status.", "error");
    } finally {
      setRowActionId(null);
    }
  };

  const availablePracticesFor = (provider) =>
    practices.filter(
      (p) => p.status === "VERIFIED"
        && !provider.affiliatedPractices?.some((ap) => ap.organizationId === p.id)
    );

  const handleAddToPractice = async (provider, organizationId) => {
    if (!organizationId) return;
    const practice = practices.find((p) => p.id === organizationId);
    setRowActionId(`${provider.id}-add`);
    try {
      await apiService.addProviderToPractice(provider.id, organizationId);
      // Adding only widens the set of practices this provider can switch
      // between — it deliberately does NOT change the practice they're
      // currently working in (see ProviderAccountController).
      showMessage(`${provider.name} added to ${practice?.name}. They can switch into it from their header.`);
      await loadAll();
    } catch (err) {
      showMessage(err.response?.data?.error || "Failed to add provider to practice.", "error");
    } finally {
      setRowActionId(null);
    }
  };

  const handleRemoveFromPractice = async (provider, organizationId, organizationName) => {
    if (!window.confirm(`Remove ${provider.name} from ${organizationName}?`)) return;
    setRowActionId(`${provider.id}-${organizationId}`);
    try {
      const result = await apiService.removeProviderFromPractice(provider.id, organizationId);
      showMessage(result.reassignedToDefault
        ? `Removed. ${provider.name} was active there, so they've been reassigned to Default Practice.`
        : `Removed ${provider.name} from ${organizationName}.`);
      await loadAll();
    } catch (err) {
      showMessage("Failed to remove provider from practice.", "error");
    } finally {
      setRowActionId(null);
    }
  };

  const handleApprovePatientRequest = async (organizationId, patientName) => {
    setRowActionId(organizationId);
    try {
      await apiService.approvePatientPracticeRequest(organizationId);
      showMessage(`Practice approved and ${patientName} assigned.`);
      await loadAll();
    } catch (err) {
      showMessage("Failed to approve practice.", "error");
    } finally {
      setRowActionId(null);
    }
  };

  const handleRejectPatientRequest = async (organizationId) => {
    if (!window.confirm("Reject and delete this proposed practice?")) return;
    setRowActionId(organizationId);
    try {
      await apiService.rejectPatientPracticeRequest(organizationId);
      showMessage("Practice request rejected.");
      await loadAll();
    } catch (err) {
      showMessage("Failed to reject practice.", "error");
    } finally {
      setRowActionId(null);
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 6, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Manage Practices & Providers
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        A practice is where a provider treats patients. It decides which patients they can see. Where a provider is
        employed or affiliated (their organizations) is managed separately, under Provider Management.
      </Typography>
      <Typography color="text.secondary" paragraph>
        Add or remove practices, manage which practices a provider belongs to, and block or
        unblock provider accounts.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {message && <Alert severity={message.severity} sx={{ mb: 3 }}>{message.text}</Alert>}

      {/* Pending practices patients have proposed */}
      {pendingPatientRequests.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Pending Practices Proposed by Patients
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "rgba(102, 126, 234, 0.08)" }}>
                  <TableCell><strong>Practice</strong></TableCell>
                  <TableCell><strong>Location</strong></TableCell>
                  <TableCell><strong>Proposed By</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingPatientRequests.map((req) => (
                  <TableRow key={req.organizationId} hover>
                    <TableCell>{req.organizationName}</TableCell>
                    <TableCell>{req.city}{req.city && req.state ? ", " : ""}{req.state}</TableCell>
                    <TableCell>{req.patientName} ({req.patientEmail})</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                        <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />}
                          disabled={rowActionId === req.organizationId}
                          onClick={() => handleRejectPatientRequest(req.organizationId)}>
                          Reject
                        </Button>
                        <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />}
                          disabled={rowActionId === req.organizationId}
                          onClick={() => handleApprovePatientRequest(req.organizationId, req.patientName)}>
                          Approve
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Add a new practice */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Add New Practice
        </Typography>
        <Paper sx={{ p: 3, maxWidth: 700 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Practice Name" value={newPractice.name}
                onChange={(e) => setNewPractice({ ...newPractice, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address" value={newPractice.addressLine1}
                onChange={(e) => setNewPractice({ ...newPractice, addressLine1: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="City" value={newPractice.city}
                onChange={(e) => setNewPractice({ ...newPractice, city: e.target.value })} />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth label="State" value={newPractice.state}
                onChange={(e) => setNewPractice({ ...newPractice, state: e.target.value })} />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth label="Zip" value={newPractice.zip}
                onChange={(e) => setNewPractice({ ...newPractice, zip: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Phone" value={newPractice.phone}
                onChange={(e) => setNewPractice({ ...newPractice, phone: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <Button fullWidth variant="contained" disabled={!newPractice.name.trim() || creatingPractice}
                onClick={handleCreatePractice}>
                {creatingPractice ? "Adding…" : "Add Practice"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* All practices */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          All Practices
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "rgba(102, 126, 234, 0.08)" }}>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Location</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {practices.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>
                    <Chip size="small" label={p.status}
                      color={p.status === "VERIFIED" ? "success" : p.status === "DEFAULT" ? "default" : "warning"} />
                  </TableCell>
                  <TableCell>{p.city}{p.city && p.state ? ", " : ""}{p.state}</TableCell>
                  <TableCell align="right">
                    {p.status !== "DEFAULT" && (
                      <Tooltip title="Delete practice (only allowed if no providers or patients are assigned to it)">
                        <span>
                          <IconButton size="small" color="error" disabled={rowActionId === p.id}
                            onClick={() => handleDeletePractice(p.id, p.name)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* All providers */}
      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          All Providers
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "rgba(102, 126, 234, 0.08)" }}>
                <TableCell><strong>Provider</strong></TableCell>
                <TableCell><strong>Active Practice</strong></TableCell>
                <TableCell><strong>Practices (treats patients at)</strong></TableCell>
                <TableCell><strong>Organizations (employed / affiliated)</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {providers.map((provider) => (
                <TableRow key={provider.id} hover>
                  <TableCell>
                    {provider.name}
                    <Typography variant="body2" color="text.secondary">{provider.email}</Typography>
                  </TableCell>
                  <TableCell>{provider.activeOrganizationName}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {provider.affiliatedPractices?.map((ap) => (
                        <Chip
                          key={ap.organizationId}
                          size="small"
                          label={ap.organizationName}
                          onDelete={() => handleRemoveFromPractice(provider, ap.organizationId, ap.organizationName)}
                          disabled={rowActionId === `${provider.id}-${ap.organizationId}`}
                        />
                      ))}
                    </Box>
                    {/* Lists every VERIFIED practice the provider isn't already in
                        — no typing required, unlike the old search-as-you-type
                        picker that showed nothing until you entered 2+ characters. */}
                    <TextField
                      select
                      size="small"
                      value=""
                      onChange={(e) => handleAddToPractice(provider, e.target.value)}
                      disabled={rowActionId === `${provider.id}-add`}
                      SelectProps={{ displayEmpty: true, renderValue: () => "Add to practice…" }}
                      sx={{ mt: 1, minWidth: 240 }}
                    >
                      {availablePracticesFor(provider).length === 0 ? (
                        <MenuItem disabled value="">
                          Already in every verified practice
                        </MenuItem>
                      ) : (
                        availablePracticesFor(provider).map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.name}{p.city ? ` — ${p.city}, ${p.state}` : ""}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <ProviderOrganizationsCell provider={provider} link={linkFor(provider.id)} onChanged={reloadProviderLinks} />
                  </TableCell>
                  <TableCell>
                    {provider.blocked
                      ? <Chip size="small" label="Blocked" color="error" />
                      : <Chip size="small" label="Active" color="success" />}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      color={provider.blocked ? "success" : "error"}
                      startIcon={<BlockIcon />}
                      disabled={rowActionId === provider.id}
                      onClick={() => handleToggleBlock(provider)}
                    >
                      {provider.blocked ? "Unblock" : "Block"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Container>
  );
}